import { useState, useCallback } from "react";
import { Modal } from "antd";
import { inventoryBackendUrl } from "../../config/gateway";

interface UseUpdateInventoryReturn {
  updateInventory: string | null;
  loading: boolean;
  error: string | null;
  fetchUpdateInventory: () => Promise<void>;
  renderModal: () => JSX.Element;
}

interface ApiResponse {
  status: number;
  message: string;
  data: string;
  response: boolean;
  ok: boolean;
}

const useUpdateInventory = (): UseUpdateInventoryReturn => {
  const [updateInventory, setUpdateInventory] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");

  const fetchUpdateInventory = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
  
    try {
      const tokenStorage = localStorage.getItem("token");
      const token = tokenStorage ? tokenStorage.replace(/['"]+/g, "") : null;
  
      if (!token) {
        throw new Error("Token de acceso no encontrado. Inicia sesión primero.");
      }
  
      const response = await fetch(
        inventoryBackendUrl.synchronize(),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message || `Error al obtener el update: ${response.status} - ${response.statusText}`
        );
      }
  
      const data: ApiResponse = await response.json();
      
      if (!data) {
        throw new Error("La respuesta del servidor está vacía.");
      }
  
      setUpdateInventory(data?.data);
      setModalMessage("La actualización de inventario se está ejecutando. Por favor espere un momento ...");
      setIsModalVisible(true);
    } catch (err) {
      if (err instanceof Error && err?.name === "AbortError") {
        setError("La solicitud tardó demasiado en responder. Inténtalo de nuevo más tarde.");
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Error desconocido al realizar la solicitud"
        );
      }
      setModalMessage("Hubo un error en la actualización del inventario. Intentalo nuevamente");
      setIsModalVisible(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const renderModal = () => (
    <Modal
      title={modalMessage === "Proceso exitoso" ? "Éxito" : "¡Importante!"}
      visible={isModalVisible}
      onOk={() => setIsModalVisible(false)}
      onCancel={() => setIsModalVisible(false)}
    >
      <p>{modalMessage}</p>
    </Modal>
  );

  return { fetchUpdateInventory, updateInventory, loading, error, renderModal };
};

export default useUpdateInventory;